<template>
    <div>
        <div v-if="loading">
            <loading></loading>
        </div>

        <div v-else>
            <b-form-select
                v-model="budgetType"
                :options="budgetTypeOptions"
                class="col-4 mb-3"
                size="sm"
                @change="getData"
            />
            <b-alert variant="danger" :show="hasErrors">
                <span v-html="errorMessage"></span>
            </b-alert>
            <b-table
                responsive
                small
                :items="filteredItems"
                :fields="fields"
                :filter="filter"
                :filter-included-fields="['search']"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                primary-key="client_id"
                @filtered="onFiltered"
            >
                <!-- HEADER -->
                <template #head(deltaPreviousAdd)="data">
                    <i class="fas fa-plus-square"></i>
                </template>
                <template #head(deltaPreviousSub)="data">
                    <i class="fas fa-minus-square"></i>
                </template>
                <template #head(deltaNextAdd)="data">
                    <i class="fas fa-plus-square"></i>
                </template>
                <template #head(deltaNextSub)="data">
                    <i class="fas fa-minus-square"></i>
                </template>
                <!-- CELLS -->
                <template #cell(previousYear)="data">
                    <span class="text-right">
                        {{ formatNumber(data.item.previousYear) }}</span
                    >
                </template>
                <template #cell(deltaPreviousAdd)="data">
                    <span class="font-italic">{{
                        formatNumber(data.item.deltaPreviousAdd)
                    }}</span>
                </template>
                <template #cell(deltaPreviousSub)="data">
                    <span class="font-italic">{{
                        formatNumber(data.item.deltaPreviousSub)
                    }}</span>
                </template>
                <template #cell(currentYear)="data">
                    {{ formatNumber(data.item.currentYear) }}
                </template>
                <template #cell(deltaNextAdd)="data">
                    <span class="font-italic">{{
                        formatNumber(data.item.deltaNextAdd)
                    }}</span>
                </template>
                <template #cell(deltaNextSub)="data">
                    <span class="font-italic">{{
                        formatNumber(data.item.deltaNextSub)
                    }}</span>
                </template>
                <template #cell(nextYear)="data">
                    {{ formatNumber(data.item.nextYear) }}
                </template>
                <!-- FOOT -->
                <template #custom-foot="data">
                    <b-tr class="bg-light">
                        <b-th>TOTAL</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.previousYear)
                        }}</b-th>
                        <b-th class="text-right font-italic">{{
                            formatNumber(total.deltaPreviousAdd)
                        }}</b-th>
                        <b-th class="text-right font-italic">{{
                            formatNumber(total.deltaPreviousSub)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.currentYear)
                        }}</b-th>
                        <b-th class="text-right font-italic">{{
                            formatNumber(total.deltaNextAdd)
                        }}</b-th>
                        <b-th class="text-right font-italic">{{
                            formatNumber(total.deltaNextSub)
                        }}</b-th>
                        <b-th class="text-right">{{
                            formatNumber(total.nextYear)
                        }}</b-th>
                    </b-tr>
                </template>
            </b-table>
            <!-- Pagination -->
            <b-row>
                <b-col md="3" class="my-1 text-center">
                    <b-pagination
                        v-if="totalRows > 0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        class="my-0"
                        size="sm"
                    ></b-pagination>
                </b-col>
                <b-col md="6" class="my-2">
                    <p class="text-center small pt-0">{{ legend }}</p>
                </b-col>
                <b-col md="3" class="my-1 text-right">
                    <b-dropdown
                        right
                        split
                        size="sm"
                        :text="perPage + ' éléments par page'"
                    >
                        <b-dropdown-item
                            v-for="pageOption in pageOptions"
                            :key="pageOption"
                            @click="setPerPage(pageOption)"
                        >
                            {{ pageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
            <!-- /Pagination -->
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import loading from '../../../common/components/loading'
import TableHandler from '../../../common/mixins/TableHandler'
import DashboardHandler from '../../mixins/DashboardHandler'

export default {
    name: 'BudgetForecastClient',

    components: {
        loading,
    },

    mixins: [TableHandler, DashboardHandler],

    data() {
        return {
            currentYear: 0,
            previousYear: 0,
            nextYear: 0,
            budgetType: 'all',
            budgetTypeOptions: [
                { value: 'all', text: 'Tout' },
                { value: 'cpt', text: 'Comptabilité' },
                { value: 'soc', text: 'Social' },
                { value: 'jur', text: 'Juridique' },
                { value: 'div', text: 'Autre' },
            ],
            fields: [],
            total: {},
        }
    },

    async created() {
        this.initYears()
        this.initFields()
        await this.getData()
    },

    methods: {
        initYears() {
            this.currentYear = this.year
            this.previousYear = this.year - 1
            this.nextYear = this.year + 1
        },

        initFields() {
            this.fields = [
                { key: 'client_name', label: 'Client', sortable: true },
            ]
            let field = {}
            field = {
                key: 'previousYear',
                label: this.previousYear.toString(),
                class: 'text-right td-dashboard-data',
                sortable: true,
            }
            this.fields.push(field)
            field = {
                key: 'deltaPreviousAdd',
                label: '+',
                class: 'text-right td-dashboard-data',
                sortable: true,
            }
            this.fields.push(field)
            field = {
                key: 'deltaPreviousSub',
                label: '-',
                class: 'text-right td-dashboard-data',
                sortable: true,
            }
            this.fields.push(field)
            field = {
                key: 'currentYear',
                label: this.currentYear.toString(),
                class: 'text-right td-dashboard-data',
                sortable: true,
            }
            this.fields.push(field)
            field = {
                key: 'deltaNextAdd',
                label: '+',
                class: 'text-right td-dashboard-data',
                sortable: true,
            }
            this.fields.push(field)
            field = {
                key: 'deltaNextSub',
                label: '-',
                class: 'text-right td-dashboard-data',
                sortable: true,
            }
            this.fields.push(field)
            field = {
                key: 'nextYear',
                label: this.nextYear.toString(),
                class: 'text-right td-dashboard-data',
                sortable: true,
            }
            this.fields.push(field)
        },

        async getData() {
            this.loading = true
            this.initYears()
            this.initFields()
            try {
                const response = await axios.get(
                    `/dashboard/budget/${this.budgetType}/forecast-client/${this.year}`
                )
                this.items = response.data.data
                this.calculateTotal()
                this.loading = false
            } catch (error) {
                console.log(error)
                this.handleErrors(error.response)
            }
        },

        initTotal() {
            this.total.previousYear = 0
            this.total.deltaPreviousAdd = 0
            this.total.deltaPreviousSub = 0
            this.total.currentYear = 0
            this.total.deltaNextAdd = 0
            this.total.deltaNextSub = 0
            this.total.nextYear = 0
        },

        calculateTotal() {
            this.initTotal()

            this.items.forEach(
                (item) => (this.total.previousYear += item.previousYear)
            )
            this.items.forEach(
                (item) => (this.total.deltaPreviousAdd += item.deltaPreviousAdd)
            )
            this.items.forEach(
                (item) => (this.total.deltaPreviousSub += item.deltaPreviousSub)
            )
            this.items.forEach(
                (item) => (this.total.currentYear += item.currentYear)
            )
            this.items.forEach(
                (item) => (this.total.deltaNextAdd += item.deltaNextAdd)
            )
            this.items.forEach(
                (item) => (this.total.deltaNextSub += item.deltaNextSub)
            )
            this.items.forEach((item) => (this.total.nextYear += item.nextYear))
        },

        formatNumber(number) {
            if (number === 0) {
                return '-'
            }
            if (number === null) {
                return '-'
            }
            return Math.floor(number).toLocaleString()
        },
    },

    computed: {
        ...mapState({
            year: (state) => state.browsing.year,
        }),
    },
}
</script>

<style scoped></style>
